import { ATTRIBUTES, ATTRIBUTE_RATINGS } from '../types';
import type { AttributeRating, OurPlayerRow, Position, TeamShape } from '../types';

/**
 * B spec §18 — **the team profile, rendered as prose a language model reads** (build unit B-27).
 *
 * ── Why this is client-side, and why that is not negotiable ──────────────────
 *
 * `service/src/coach.ts` states the property this module exists to preserve: the Worker *"holds no
 * domain vocabulary: it does not know the seven phases, the match formats or the purposes… An
 * eighth phase is therefore a client-only change."* Sending positions and ratings as structured
 * data would force the system prompt to teach the model that `CB` means the middle of a back three
 * **in this coach's shorthand**, that a missing rating means NOT ASSESSED, that `needsWork` is a
 * constraint on what to *ask*, that an empty secondary list is a **prohibition**, and the gloss for
 * nineteen camelCase keys and fifteen position codes. That is the entire domain vocabulary of the
 * amendment, permanently resident in the Worker, with every later change becoming a coordinated
 * two-package deploy whose failure is **silent** — the model meets a key it has no gloss for and
 * quietly misreads it.
 *
 * §3.7.39 also simply says what it says: *"rendered as readable prose rather than a rating
 * matrix."* A sparse JSON map of ratings **is** a rating matrix.
 *
 * ── Pure, like `domain/coach.ts`'s validator ─────────────────────────────────
 *
 * No I/O, no clock, no DOM. Driven end to end by the Node suite, which is why the size assertion in
 * §18.2 is *asserted* here rather than reasoned about in the spec.
 *
 * ── Three rules the output obeys ─────────────────────────────────────────────
 *
 * 1. **Absence is silence** (§3.7.37). No text is ever emitted for an unrated attribute — no
 *    "unrated", no "not assessed:", no placeholder, no em dash. An empty rating bucket emits **no
 *    line at all** rather than an empty one.
 * 2. **One deliberate exception, and it is the safety property** (§3.7.36). An empty secondary list
 *    is a *value*, not a blank. Every player gets a position sentence unconditionally; only its
 *    wording varies, through a **two-arm switch with no third arm**. Silence here would read to the
 *    model as "no information", and it would cheerfully suggest the move.
 * 3. **The reading rules travel with the data they govern**, in a header — because they cannot go
 *    in the system prompt without teaching the Worker what a secondary position is.
 *
 * ── Ruling R-1 — the referent carries the name ───────────────────────────────
 *
 * §18.3 recommended `#7` alone on the wire. **The stakeholder overruled it**: *"First names are
 * used throughout youth sports… That's not PII in any true meaning of PII… Change the rule, don't
 * change my spec for the player data or what gets transmitted."* So the referent is `#7 Eden`,
 * which also **dissolves** accepted residual (a) — two `#7`s no longer produce an ambiguous
 * referent, because the name disambiguates them on the wire itself.
 */

/** §18.2's header. The reading rules, travelling with the data they govern. */
const PROFILE_HEADER =
  'OUR TEAM\n\n' +
  "Position codes are this coach's own — read them against the shapes below, not against a\n" +
  'textbook. Nothing is said about a player unless the coach has assessed it; an attribute that\n' +
  'does not appear has not been assessed, either way.';

/**
 * §18.2 — the rating group labels, **as an exhaustive `Record`**.
 *
 * Exhaustive rather than a `switch` or a map literal typed `Record<string, string>` for one
 * reason: a fourth `AttributeRating` added without a phrase here is a **compile error**, not a
 * bucket that renders with no heading. The three phrases are the semantics — the model reads them,
 * not the enum:
 *
 * - `great → Strong:` — what she is good at.
 * - `okay → Adequate:` — what she can be asked for without comment.
 * - `needsWork → Do not ask for:` — **§3.7.37's own framing** (*"a constraint on what to ask of a
 *   player"*) rendered as an instruction. Without that framing a model reads "needs work" as a
 *   coaching to-do for the break, which is the **opposite** instruction.
 */
const RATING_PHRASE: Readonly<Record<AttributeRating, string>> = {
  great: 'Strong:',
  okay: 'Adequate:',
  needsWork: 'Do not ask for:',
};

/**
 * §18.2(d) — the four fixed strings the shape block supplies. Everything else in that block is
 * **the user's own words, unedited**.
 *
 * *"Moving between them:"* rather than *"Transition:"* deliberately: `attTransition` /
 * `defTransition` are phase values that mean something else, and colliding with them in prose the
 * model also reads phase names in would be a gloss nobody wrote down.
 */
const SHAPE_HEADING = 'OUR SHAPE';
const SHAPE_OUT_OF_POSSESSION = 'Out of possession we set up in';
const SHAPE_IN_POSSESSION = 'In possession we become';
const SHAPE_FRAMING = 'These are two phases of the same team, not two teams.';
const SHAPE_TRANSITION = 'Moving between them:';

/**
 * The referent for one player, ruling R-1's form: `#7 Eden`.
 *
 * Exported because §18.4's card-side join needs the **same** answer, and two spellings of "how this
 * app names a player" would eventually disagree — the failure mode the `ATTRIBUTES` table's "one
 * table, three readers" note exists to prevent, applied to the referent.
 *
 * **Duplication-ledger note, written before the detector finds it**: this is deliberately NOT
 * `domain/file.playerLabel`. That names an **opponent** shirt-number stub under ENV-11's
 * no-identity rule, which has no name to render; this names one of *our own* squad, which under
 * R-1 always does. Two different kinds of player. Nobody should "consolidate" them.
 */
export function ourPlayerLabel(player: Pick<OurPlayerRow, 'jerseyNumber' | 'firstName'>): string {
  const name = player.firstName.trim();
  return name === '' ? `#${String(player.jerseyNumber)}` : `#${String(player.jerseyNumber)} ${name}`;
}

/**
 * §18.2's position sentence — **the two-arm switch with no third arm** (rule 2 above).
 *
 * `Do not move from CM.` is the load-bearing sentence in the whole design. §3.7.36: *"An empty
 * secondary list means that player must not be moved at all. This is a safety property, not a
 * convenience."*
 */
function positionSentence(primary: Position, secondaries: readonly Position[]): string {
  return secondaries.length === 0
    ? `Do not move from ${primary}.`
    : `Can also play ${secondaries.join(', ')}.`;
}

/**
 * One player's rating lines, grouped **by rating** rather than by phase of play.
 *
 * The coaching question is *"what can I ask of her"*, so grouping this way puts every `needs work`
 * in one place the prompt can point at. Within each group the order is `ATTRIBUTES`' order, which
 * is why two players rated in different orders produce identically ordered prose (§15.2).
 *
 * A bucket with nothing in it produces **no line** — not an empty one (§18.2(b)).
 */
function ratingLines(ratings: OurPlayerRow['ratings']): string[] {
  const buckets = new Map<AttributeRating, string[]>(
    ATTRIBUTE_RATINGS.map((rating) => [rating, []]),
  );
  for (const attribute of ATTRIBUTES) {
    const rating = ratings[attribute.key];
    if (rating === undefined) continue;
    buckets.get(rating)?.push(attribute.label);
  }
  const lines: string[] = [];
  for (const rating of ATTRIBUTE_RATINGS) {
    const labels = buckets.get(rating) ?? [];
    if (labels.length === 0) continue;
    lines.push(`  ${RATING_PHRASE[rating]} ${labels.join(ITEM_SEPARATOR)}.`);
  }
  return lines;
}

/**
 * **A semicolon, not a comma, and this is the one place the implementation departs from §18.2's
 * literal example.**
 *
 * Found by assembling the renderer's output and *reading* it rather than by asserting its fields:
 * **two of the nineteen labels contain a comma of their own** — *"passes accurately, short"*
 * (`types/index.ts:637`) and *"holds position, doesn't get dragged"* (`:650`) — so a comma-joined
 * list renders
 *
 * > `Adequate: keeps it under pressure, passes accurately, short, wins duels, …`
 *
 * in which *"short"* reads as a **fourth attribute**. §18.2's illustrated output has the same
 * ambiguity, and the labels are not negotiable: §3.7.37's wording is a stakeholder ruling and
 * §15.2 requires it character for character, so the separator is the only half that can move.
 *
 * The separator is the renderer's own, not the requirement's, and a model reading a list of
 * comma-containing phrases separated by semicolons cannot mis-split it. Every requirement-owned
 * string is untouched.
 */
const ITEM_SEPARATOR = '; ';

/** One player's whole block: the position line always, then whatever ratings exist. */
function playerBlock(player: OurPlayerRow): string {
  const head =
    `${ourPlayerLabel(player)} — normally ${player.primaryPosition}. ` +
    positionSentence(player.primaryPosition, player.secondaryPositions);
  return [head, ...ratingLines(player.ratings)].join('\n');
}

/**
 * §18.2(d) — the shape block, or `null` when the user has recorded nothing.
 *
 * A blank field emits no line; all three blank emits **no block at all**. The framing sentence is
 * §3.7.39's second clause, and it is emitted whenever either shape is present because that is the
 * clause's whole purpose — *"presented as phases of one team rather than alternatives"*.
 */
function shapeBlock(shape: TeamShape): string | null {
  const outOfPossession = shape.outOfPossession.trim();
  const inPossession = shape.inPossession.trim();
  const transition = shape.transition.trim();
  if (outOfPossession === '' && inPossession === '' && transition === '') return null;

  const sentences: string[] = [];
  if (outOfPossession !== '') sentences.push(`${SHAPE_OUT_OF_POSSESSION} ${outOfPossession}.`);
  if (inPossession !== '') sentences.push(`${SHAPE_IN_POSSESSION} ${inPossession}.`);
  if (sentences.length > 0) sentences.push(SHAPE_FRAMING);

  const paragraphs = [SHAPE_HEADING];
  if (sentences.length > 0) paragraphs.push(sentences.join(' '));
  if (transition !== '') paragraphs.push(`${SHAPE_TRANSITION} ${transition}`);
  return paragraphs.join('\n\n');
}

/**
 * §18 — the whole profile as prose, or `null` when there is nothing to say.
 *
 * @param players the **active** roster, in the order it should be read (jersey ascending; the repo
 *   comparator already produces that). Inactive players are the caller's to filter — §17.1 makes
 *   *"not sent to Coach Claude"* the visible consequence of the toggle, so the filter belongs where
 *   the user can see it, not hidden in here.
 * @param shape the team shape. An empty shape contributes no block.
 * @returns the prose, or `null` when there are **no active players and no shape** — which is
 *   exactly the condition §18.5's disclosure calls `'empty'`, and `buildCoachRequestB` sends as
 *   `team: null`.
 */
export function renderTeamProfile(
  players: readonly OurPlayerRow[],
  shape: TeamShape,
): string | null {
  const shapeText = shapeBlock(shape);
  if (players.length === 0 && shapeText === null) return null;

  const blocks: string[] = [];
  if (players.length > 0) {
    blocks.push(PROFILE_HEADER, ...players.map(playerBlock));
  }
  if (shapeText !== null) blocks.push(shapeText);
  return blocks.join('\n\n');
}

/**
 * §17.1 + §18.5 — the facts *about* the profile that no rendered profile carries.
 *
 * Lives here rather than in either reader because there are **two** readers of one derivation:
 * §17.1's freshness line on the My Team tab, and §18.5's disclosure above Generate. Putting it in
 * either would make the other import across a feature boundary for a fact that belongs to neither.
 *
 * - `activeCount` / `totalCount` — §18.5's count, and it is a **mitigation for a defect shape this
 *   project has already been bitten by once** (B-22): a player the user forgot to mark active is
 *   absent from the payload with no symptom but suggestions that feel generic. *"13 of 15
 *   players"* makes that two-player gap readable at the moment it can still be fixed.
 * - `lastChanged` — `max(updatedAt)` over the **active** players **and the shape together**, which
 *   is the whole of pre-mortem PM-1: a stamp covering the roster but not the shape would read
 *   *"last changed today"* over a shape a season old. `null` when nothing has ever been written.
 *   Ruling R-3 is what makes the shape half of this possible, by putting `updatedAt` inside the
 *   setting's value.
 */
export interface TeamProfileFacts {
  readonly activeCount: number;
  readonly totalCount: number;
  readonly lastChanged: number | null;
}

export function teamProfileFacts(
  players: readonly OurPlayerRow[],
  shape: TeamShape,
): TeamProfileFacts {
  const active = players.filter((player) => player.active);
  const stamps = [...active.map((player) => player.updatedAt), shape.updatedAt].filter(
    (stamp) => stamp > 0,
  );
  return {
    activeCount: active.length,
    totalCount: players.length,
    lastChanged: stamps.length === 0 ? null : Math.max(...stamps),
  };
}

/**
 * §18.4 — the **card-side** join: `#7` in the model's own words becomes `#7 Eden` on screen.
 *
 * Applied at render time only, **never to the stored text**, so §12's existing negative — *"a
 * finalized card's text never changes afterward"* — holds unchanged.
 *
 * ── Idempotent, and that is required rather than tidy ────────────────────────
 *
 * Under ruling R-1 the wire already carries `#7 Eden`, so the model will frequently write the name
 * itself. A naive substitution would render `#7 Eden Eden`. A `#N` already followed by that
 * player's name is therefore left exactly as it is — which also makes applying this function twice
 * indistinguishable from applying it once.
 *
 * A `#N` whose number is **not** on the roster is left alone: the model may be talking about an
 * opponent's shirt, and inventing one of our names over it would be worse than saying nothing.
 * A `#N` shared by two players is also left alone rather than guessed at — jersey numbers are not
 * unique (§15.3), and picking one of two names would assert something the data does not support.
 *
 * ── The already-named test looks FORWARD, and does not tokenise ──────────────
 *
 * The first implementation captured *a word* after the number and compared it to the name. That
 * asked the wrong question, and both halves of it were wrong in opposite directions (milestone
 * review A-1). A character class permissive enough to hold `O'Hara` also swallowed the `'s` of
 * `#7 Eden's first touch`, whose comparison then failed and rendered **`#7 Eden Eden's`** — on the
 * line a coach reads out loud. Tightening the class to fix that would have broken `O'Hara` and
 * `Anne-Marie` the same way, in the other direction. And a word capture cannot see the name at all
 * across punctuation, so `#7, Eden,` and `#7 (Eden)` garbled too.
 *
 * So this asks the only question that has an answer: **does the text after `#N` already name this
 * player?** — matching the roster name whole, so its own apostrophes and hyphens are part of what
 * is being looked for rather than characters a tokeniser has to guess about. Everything else falls
 * out of {@link alreadyNamed}'s two boundaries.
 */
export function nameJerseyRefs(text: string, players: readonly OurPlayerRow[]): string {
  const byNumber = new Map<number, string | null>();
  for (const player of players) {
    const name = player.firstName.trim();
    // `null` marks a number two players share — ambiguous, so never substituted.
    byNumber.set(player.jerseyNumber, byNumber.has(player.jerseyNumber) ? null : name);
  }
  const alreadyNamedBy = new Map<string, RegExp>();

  return text.replace(/#(\d{1,2})\b/gu, (match: string, digits: string, offset: number) => {
    const name = byNumber.get(Number(digits));
    if (name === undefined || name === null || name === '') return match;
    let follows = alreadyNamedBy.get(name);
    if (follows === undefined) {
      follows = alreadyNamed(name);
      alreadyNamedBy.set(name, follows);
    }
    if (follows.test(text.slice(offset + match.length))) return match;
    return `#${digits} ${name}`;
  });
}

/**
 * `RegExp` metacharacters, escaped — `firstName` is user input and a name like `O'Brien (Jr)` would
 * otherwise throw a `SyntaxError` while rendering a card at a venue.
 *
 * The set is the syntax characters only. `-` is deliberately **not** escaped: this pattern is built
 * for use outside a character class, where `\-` is not a legal identity escape under the `u` flag
 * and would itself throw. Outside a class `-` is already a literal.
 */
function escapeForRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}

/**
 * The test {@link nameJerseyRefs} runs against the text **following** a `#N`: does it already name
 * this player? Two boundaries carry the whole behaviour.
 *
 * **Leading `[^\p{L}\p{N}]*` — anything that is not a letter or a digit may sit between the number
 * and the name.** That is what lets `#7, Eden,` and `#7 (Eden)` be recognised as already named
 * instead of doubled. It is deliberately a *negated* class rather than a list of the separators
 * anyone thought of, and it cannot run away: it stops at the first letter or digit, so it can
 * never skip past the next jersey number to find a name belonging to someone else. `#7's first
 * touch` is the same rule read the other way — the `'` is skipped, `s first touch` is not the
 * name, so the name is inserted and the possessive is left where the model put it.
 *
 * **Trailing `(?![\p{L}\p{M}])` — the name must end where it ends.** Without it a roster `Ed`
 * would read `#7 Edward` as already named and say nothing.
 *
 * The name is matched **whole and case-insensitively**, which is the half that survives a roster
 * name containing punctuation: `O'Hara` and `Anne-Marie` are looked for exactly as spelled, so
 * neither is half-matched and then doubled.
 *
 * Known and deliberate: a model that *abbreviates* a roster name (`#5 Anne` for `Anne-Marie`)
 * still gets the full name inserted, rendering `#5 Anne-Marie Anne`. Recognising an abbreviation
 * means guessing which prefix of a name is a name, and this function's whole posture is to guess
 * nothing — the same posture that leaves a shared number alone.
 */
function alreadyNamed(name: string): RegExp {
  return new RegExp(`^[^\\p{L}\\p{N}]*${escapeForRegExp(name)}(?![\\p{L}\\p{M}])`, 'iu');
}
